"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Modal, Button, Form, Alert, Spinner, Badge } from "react-bootstrap"
import { FaExclamationTriangle } from "react-icons/fa"
import axios from "axios"
import "../../../css/promoCodeModal.css"
import type { AppContextType } from "../../../context/AppContext"
import useAppContext from "../../../hooks/useAppContext"
import Swal from "sweetalert2"

interface PromoCodeModalProps {
  show: boolean
  onHide: () => void
  onSuccess: (
    finalDiscountPercentage: number,
    originalDiscountPercentage: number,
    isCapped: boolean,
    promoCodeUsed: string
  ) => void
  userId: string
}

interface PromoCodeResponse {
  valid: boolean
  message: string
  promoCode: {
    id: number
    code: string
    description: string
    discountPercentage: number
  }
}

interface UserDiscountLimit {
  percentage: number
  active: boolean
}

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({ show, onHide, onSuccess, userId }) => {
  const { discountId, setDiscountId, setDiscountAmount } = useAppContext() as AppContextType
  const [promoCode, setPromoCode] = useState<string>("")
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [verifiedCode, setVerifiedCode] = useState<PromoCodeResponse | null>(null)
  const [step, setStep] = useState<"verify" | "confirm">("verify")
  const [userDiscountLimit, setUserDiscountLimit] = useState<UserDiscountLimit | null>(null)
  const [limitLoading, setLimitLoading] = useState<boolean>(false)

  // Récupérer la limite de l'utilisateur quand le modal s'ouvre
  useEffect(() => {
    if (show && userId) {
      fetchUserDiscountLimit()
    }
  }, [show, userId])

  const fetchUserDiscountLimit = async () => {
    setLimitLoading(true)
    try {
      const token = localStorage.getItem("token")
      if (!token) return

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/discount-limit/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      })

      if (response.data && response.data.discountLimit) {
        setUserDiscountLimit(response.data.discountLimit)
      }
    } catch (err: any) {
      setUserDiscountLimit({ percentage: 0, active: false })
    } finally {
      setLimitLoading(false)
    }
  }

  // ----- ETAPE 1 : Vérification du code promo -----
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!promoCode.trim()) {
      setError("Veuillez entrer un code promotionnel")
      return
    }
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("token")
      const verifyResponse = await axios.post<PromoCodeResponse>(
        `${process.env.REACT_APP_API_URL}/promo-code/verify`,
        { code: promoCode, userId },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      // Cas d'échec (code non valide, déjà utilisé, ...):
      if (!verifyResponse.data.valid) {
        setError(verifyResponse.data?.message || "Code promo non valide ou déjà utilisé.")
        return
      }
      // Sinon : on prépare l'étape de confirmation
      const originalDiscount = verifyResponse.data.promoCode.discountPercentage
      let finalDiscount = originalDiscount
      let isCapped = false

      // Appliquer la limite de l'utilisateur si elle est active et applicable
      if (userDiscountLimit && userDiscountLimit.active && userDiscountLimit.percentage > 0) {
        if (originalDiscount > userDiscountLimit.percentage) {
          finalDiscount = userDiscountLimit.percentage
          isCapped = true
        }
      }

      setVerifiedCode({
        ...verifyResponse.data,
        promoCode: {
          ...verifyResponse.data.promoCode,
          discountPercentage: finalDiscount, // Stocke la remise plafonnée
          originalDiscountPercentage: originalDiscount, // Stocke l'original pour l'affichage
          isCapped: isCapped, // Indique si le plafonnement a eu lieu
        },
      } as PromoCodeResponse) // Cast pour inclure les nouvelles propriétés
      setStep("confirm")
    } catch (err: any) {
      setError(err.response?.data?.message || "Erreur lors de la vérification du code promo")
    } finally {
      setLoading(false)
    }
  }

  const handleApply = async () => {
  if (!verifiedCode) {
    setError("Erreur : le code promo n'a pas été vérifié")
    return
  }
  
  // NE PAS appliquer côté serveur - juste passer en "pending"
  onSuccess(
    verifiedCode.promoCode.discountPercentage,
    (verifiedCode.promoCode as any).originalDiscountPercentage,
    (verifiedCode.promoCode as any).isCapped,
    verifiedCode.promoCode.code
  )
  
  // Stocker l'ID pour usage ultérieur
  setDiscountId( verifiedCode.promoCode.id )
  setDiscountAmount( verifiedCode.promoCode.discountPercentage )
  
  // Afficher message informatif
  Swal.fire({
    title: "Code promo sélectionné",
    text: `Votre code promo sera appliqué lors de la validation de l'annonce.`,
    icon: "info",
    timer: 2000,
    toast: true,
    position: "top-right"
  })
  
  onHide()
  setPromoCode("")
  setVerifiedCode(null)
  setStep("verify")
}
  const handleCancel = () => {
    setPromoCode("")
    setVerifiedCode(null)
    setStep("verify")
    onHide()
  }

  const handleBack = () => {
    setStep("verify")
    setVerifiedCode(null)
  }

  const getDiscountBadgeVariant = (percentage: number) => {
    if (percentage === 100) return "success"
    if (percentage >= 50) return "warning"
    return "danger"
  }

  return (
    <Modal show={show} onHide={handleCancel} centered className="promo-code-modal">
      <Modal.Header closeButton>
        <Modal.Title>{step === "verify" ? "Utiliser un code promotionnel" : "Confirmer le code promo"}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="model-body">
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}
        {/* Affichage de la limite utilisateur */}
        {/* {!limitLoading && (
          <div className="mb-3 p-3 bg-light rounded">
            {userDiscountLimit && userDiscountLimit.active && userDiscountLimit.percentage > 0 && (
              <>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small text-muted">Votre promotion est plafonnée par l'administrateur</span>
                </div>
                <div className="mt-2">
                  <Alert variant="info" className="small mb-0">
                    <FaExclamationTriangle className="me-1" />
                    Votre réduction est automatiquement plafonnée à {userDiscountLimit.percentage}%. Restez fidèle pour bénéficier d'une offre exclusive de fidélité.
                  </Alert>
                </div>
              </>
            )}
          </div>
      )} */}

        {step === "verify" ? (
          <Form onSubmit={handleVerify}>
            <Form.Group className="mb-3">
              <Form.Label>Code promotionnel</Form.Label>
              <Form.Control
                type="text"
                placeholder="Entrez votre code promo"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                disabled={loading || limitLoading}
                autoFocus
                className="promo-code-input"
              />
              <Form.Text className="text-muted">
                Entrez le code promotionnel que vous avez reçu pour bénéficier d'une remise sur votre annonce.
              </Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end">
              <Button variant="secondary" onClick={handleCancel} className="me-2" disabled={loading || limitLoading}>
                Annuler
              </Button>
              <Button variant="primary" type="submit" disabled={loading || limitLoading} className="verify-button">
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Vérification...
                  </>
                ) : (
                  "Vérifier"
                )}
              </Button>
            </div>
          </Form>
        ) : (
          <div className="confirmation-container">
            <div className="code-details">
              <h4 className="code-value">{verifiedCode?.promoCode.code}</h4>
              {verifiedCode?.promoCode.description && (
                <p className="code-description">{verifiedCode.promoCode.description}</p>
              )}
              <div className="discount-info">
                <Badge
                  bg={getDiscountBadgeVariant(verifiedCode?.promoCode.discountPercentage || 0)}
                  className="discount-badge-large"
                >
                  {verifiedCode?.promoCode.discountPercentage}% de remise
                </Badge>
                {(verifiedCode?.promoCode as any).isCapped && (
                  <p className="text-warning small mt-2">
                    <FaExclamationTriangle className="me-1" />
                    Remise plafonnée à {(verifiedCode?.promoCode as any).discountPercentage}% (originale :{" "}
                    {(verifiedCode?.promoCode as any).originalDiscountPercentage}%) selon votre limite personnalisée.
                  </p>
                )}
                <p className="discount-explanation">
                  {verifiedCode?.promoCode.discountPercentage === 100
                    ? "Ce code vous donne droit à une annonce gratuite."
                    : `Ce code vous donne droit à une remise de ${verifiedCode?.promoCode.discountPercentage}% sur le prix de votre annonce.`}
                </p>
              </div>
            </div>
            <div className="d-flex justify-content-end mt-4">
              <Button variant="outline-secondary" onClick={handleBack} className="me-2" disabled={loading}>
                Retour
              </Button>
              <Button variant="primary" onClick={handleApply} disabled={loading} className="apply-button">
                {loading ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Application...
                  </>
                ) : (
                  "Appliquer ce code"
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default PromoCodeModal